/**
 * Cola de facturas para salesCounter.
 * - Las facturas se encolan en IndexedDB y se procesan en background.
 * - Al encolar, el formulario ya se puede reiniciar (guardado silencioso).
 * - Reintenta automáticamente (máx 3 veces) con backoff exponencial.
 * - Funciona sin conexión: si la llamada falla, queda como "failed" y
 *   se reintenta cuando se llama a retryFailed() o startProcessing().
 */

import serviceAPI from '../services'
import queueStorage from './queueStorage'

const MAX_ATTEMPTS = 3
const PROCESSING_STALE_MS = 2 * 60 * 1000
const RECENT_DONE_LIMIT = 5

class SaleQueue {
  constructor() {
    this._queue      = []
    this._recentDone = []
    this._lastSyncAt = null
    this._ready      = false
    this._loading    = false
    this._loadPromise = null
    this._processing = false
    this._online     = false
    this._listeners  = []
    this._printCb    = null   // callback(responseData) para imprimir después de guardar
    this._loadPromise = this._loadInitial()
  }

  // ── Registro de callbacks ────────────────────────────────────────────────

  /** fn(stats) — se llama cada vez que cambia el estado de la cola */
  onUpdate(fn) {
    this._listeners.push(fn)
    return () => { this._listeners = this._listeners.filter(l => l !== fn) }
  }

  /** fn(responseData) — se llama cuando una factura se guarda exitosamente. Retorna unsubscribe. */
  onSaved(fn) {
    this._printCb = fn
    return () => { if (this._printCb === fn) this._printCb = null }
  }

  // ── API pública ──────────────────────────────────────────────────────────

  /** Agrega una factura a la cola y dispara el procesamiento en background */
  enqueue(saleData) {
    const item = {
      id:          `${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
      saleData:    JSON.parse(JSON.stringify(saleData)),  // copia profunda
      enqueuedAt:  new Date().toISOString(),
      attempts:    0,
      status:      'pending',   // pending | processing | failed | done
      error:       null,
      lastAttemptAt: null,
      lastErrorAt: null,
      syncStage:    'local_registered',
      fiscalStatus: 'pending',
      printStatus:  this._shouldPrintTicket(saleData) ? 'pending' : 'not_required',
      meta:        this._buildMeta(saleData),
    }
    this._queue.push(item)
    this._persistItem(item)
    this._notify()
    if (this._online) {
      setTimeout(() => this._processNext(), 0)
    }
    return item.id
  }

  setOnline(online) {
    const wasOnline = this._online
    this._online = online === true
    if (this._online && !wasOnline) {
      this.startProcessing()
    }
  }

  /** Reintenta todos los ítems fallidos */
  retryFailed() {
    this._queue.forEach(item => {
      if (item.status === 'failed') {
        item.status   = 'pending'
        item.attempts = 0
        item.error    = null
        item.lastErrorAt = null
        this._persistItem(item)
      }
    })
    this._notify()
    if (this._online) this._processNext()
  }

  /** Inicia el procesamiento (útil al reconectar) */
  startProcessing() {
    if (!this._online) return
    this._whenReady(() => {
      this._recoverStaleProcessing()
      this._processNext()
    })
  }

  getStats() {
    const pending    = this._queue.filter(i => i.status === 'pending').length
    const processing = this._queue.filter(i => i.status === 'processing').length
    const failed     = this._queue.filter(i => i.status === 'failed').length
    const done       = this._queue.filter(i => i.status === 'done').length
    const staleProcessing = this._queue.filter(i => this._isStaleProcessing(i)).length
    const openItems  = this._queue.filter(i => i.status === 'pending' || i.status === 'processing' || i.status === 'failed')
    const openTotal  = openItems.reduce((sum, i) => sum + this._saleTotal(i.saleData), 0)
    return { total: this._queue.length, pending, processing, failed, done, staleProcessing, openTotal, lastSyncAt: this.getLastSyncAt() }
  }

  getOpenItems() {
    return this._queue
      .filter(i => i.status === 'pending' || i.status === 'processing' || i.status === 'failed')
      .map(i => this._describeItem(i))
  }

  getOpenItemsForCashIds(cashIds) {
    const ids = (cashIds || []).map(id => parseInt(id)).filter(Boolean)
    if (!ids.length) return []
    return this.getOpenItems().filter(item => item.cashIds.some(id => ids.includes(parseInt(id))))
  }

  hasBlockingItems() {
    return this._queue.some(i => i.status === 'pending' || i.status === 'processing' || i.status === 'failed')
  }

  hasBlockingItemsForCash(cashId) {
    const id = parseInt(cashId)
    if (!id) return false
    return this.getOpenItemsForCashIds([id]).length > 0
  }

  getLastSyncAt() {
    return this._lastSyncAt
  }

  getRecentDoneItems() {
    return this._recentDone.slice()
  }

  recoverStaleProcessing() {
    const recovered = this._recoverStaleProcessing(true)
    if (recovered && this._online) this._processNext()
    return recovered
  }

  checkStorageHealth() {
    return queueStorage.checkHealth()
  }

  /** Descarta definitivamente un ítem fallido de la cola */
  discardItem(id) {
    this._queue = this._queue.filter(i => i.id !== id)
    this._deleteItem(id)
    this._notify()
  }

  /** Reintenta únicamente el ítem indicado */
  retryItem(id) {
    const item = this._queue.find(i => i.id === id)
    if (item && item.status === 'failed') {
      item.status   = 'pending'
      item.attempts = 0
      item.error    = null
      item.lastErrorAt = null
      this._persistItem(item)
      this._notify()
      if (this._online) this._processNext()
    }
  }

  // ── Procesamiento interno ────────────────────────────────────────────────

  async _processNext() {
    if (!this._ready) {
      this._whenReady(() => this._processNext())
      return
    }
    if (!this._online) return
    if (this._processing) return

    const next = this._queue.find(
      i => i.status === 'pending' || (i.status === 'failed' && i.attempts < MAX_ATTEMPTS)
    )
    if (!next) return

    this._processing   = true
    next.status        = 'processing'
    next.syncStage     = 'syncing'
    next.lastAttemptAt = new Date().toISOString()
    this._persistItem(next)   // persiste antes del POST para detectar crash mid-flight
    this._notify()

    try {
      const response = await serviceAPI.agregarComprobante(next.saleData)
      next.status    = 'done'
      next.savedData = response.data
      next.syncStage = 'server_saved'
      next.fiscalStatus = this._saleFiscalStatus(response.data)
      next.printStatus = this._shouldPrintTicket(next.saleData) ? 'pending' : 'not_required'
      this._markLastSync()
      this._pushRecentDone(next, response.data)
      this._deleteItem(next.id)
      this._notify()

      if (next.saleData._idempotency_key) {
        serviceAPI.deleteSyncErrors(next.saleData._idempotency_key).catch(() => {})
      }

      // Notificar para impresión sólo si fue solicitada.
      if (this._printCb && this._shouldPrintTicket(next.saleData)) {
        try {
          this._printCb(response.data, next.saleData)
          this._markRecentPrintStatus(response.data, 'requested')
        } catch(e) {
          this._markRecentPrintStatus(response.data, 'failed')
          console.warn('[SaleQueue] print callback error:', e)
        }
      }

      // Limpiar ítems "done" después de 30 segundos
      setTimeout(() => this._purgeDone(), 30000)

    } catch(e) {
      next.attempts++
      next.status = next.attempts >= MAX_ATTEMPTS ? 'failed' : 'pending'
      next.syncStage = next.status === 'failed' ? 'sync_failed' : 'local_registered'
      next.error  = e.response ? JSON.stringify(e.response.data) : e.message
      next.lastErrorAt = new Date().toISOString()

      if (next.status === 'failed') {
        console.error('[SaleQueue] Item fallido definitivamente:', next.id, '\nError:', next.error, '\nData:', next.saleData)
      }

      serviceAPI.logSyncError({
        idempotency_key: next.saleData._idempotency_key || next.id,
        staff_id:        next.saleData.staff_id || null,
        attempt:         next.attempts,
        error_message:   next.error,
        sale_data:       next.saleData,
        occurred_at:     next.lastErrorAt,
      }).catch(() => {})

      this._persistItem(next)
      this._notify()

      // Backoff exponencial: 3s, 9s, 27s
      if (next.status === 'pending') {
        const delay = Math.pow(3, next.attempts) * 1000
        setTimeout(() => this._triggerNext(), delay)
      }
    } finally {
      this._processing = false
    }

    // Si el ítem quedó 'pending' con backoff, no disparar inmediato (el setTimeout del catch ya lo maneja)
    if (next.status !== 'pending') {
      this._triggerNext()
    }
  }

  _triggerNext() {
    const hasPending = this._queue.some(i => i.status === 'pending')
    if (hasPending && this._online) setTimeout(() => this._processNext(), 100)
  }

  _purgeDone() {
    this._queue = this._queue.filter(i => i.status !== 'done')
    this._notify()
  }

  // ── Persistencia ─────────────────────────────────────────────────────────

  async _loadInitial() {
    if (this._ready) return
    if (this._loading) return this._loadPromise
    this._loading = true
    try {
      const [storedQueue, lastSyncAt] = await Promise.all([
        queueStorage.loadQueue(),
        queueStorage.loadLastSync(),
      ])
      const currentById = {}
      this._queue.forEach(item => { currentById[item.id] = item })
      const merged = (storedQueue || []).map(item => currentById[item.id] || item)
      this._queue.forEach(item => {
        if (!merged.find(stored => stored.id === item.id)) merged.push(item)
      })

      // Al cargar, los ítems "processing" que quedaron del anterior crash vuelven a "pending"
      merged.forEach(i => {
        if (!i.meta) {
          i.meta = this._buildMeta(i.saleData)
          this._persistItem(i)
        }
        if (i.status === 'processing') {
          i.status = 'pending'
          i.syncStage = 'local_registered'
          i.error = i.error || 'Se recuperó un guardado interrumpido'
          this._persistItem(i)
        }
      })
      this._queue = merged
      this._lastSyncAt = lastSyncAt
      this._ready = true
      this._notify()
      if (this._online) this._processNext()
    } catch(e) {
      this._ready = true
      this._notify()
      console.warn('[SaleQueue] initial load error:', e)
    } finally {
      this._loading = false
    }
  }

  _whenReady(fn) {
    if (this._ready) {
      fn()
      return
    }
    (this._loadPromise || this._loadInitial()).then(fn).catch(e => {
      console.warn('[SaleQueue] deferred action error:', e)
    })
  }

  _persistItem(item) {
    if (!item || item.status === 'done') return
    if (!item.meta) item.meta = this._buildMeta(item.saleData)
    queueStorage.saveItem(JSON.parse(JSON.stringify(item))).then(saved => {
      if (!saved) console.warn('[SaleQueue] item persistence failed:', item.id)
    })
  }

  _deleteItem(id) {
    queueStorage.deleteItem(id).then(deleted => {
      if (!deleted) console.warn('[SaleQueue] item delete failed:', id)
    })
  }

  _markLastSync() {
    this._lastSyncAt = new Date().toISOString()
    queueStorage.saveLastSync(this._lastSyncAt).then(saved => {
      if (!saved) console.warn('[SaleQueue] last sync persistence failed')
    })
  }

  _pushRecentDone(item, responseData) {
    const done = this._describeItem({ ...item, status: 'done' })
    done.savedId = responseData && responseData.id ? responseData.id : null
    done.syncedAt = new Date().toISOString()
    done.syncStage = 'server_saved'
    done.fiscalStatus = this._saleFiscalStatus(responseData)
    const shouldPrint = this._shouldPrintTicket(item.saleData)
    done.printStatus = !shouldPrint
      ? 'not_required'
      : (item.saleData && !item.saleData.from_electronic_sale)
        ? 'local'
        : (item.printStatus || 'pending')
    this._recentDone.unshift(done)
    this._recentDone = this._recentDone.slice(0, RECENT_DONE_LIMIT)
  }

  _markRecentPrintStatus(responseData, status) {
    const savedId = responseData && responseData.id ? responseData.id : null
    if (!savedId) return
    const item = this._recentDone.find(i => parseInt(i.savedId) === parseInt(savedId))
    if (!item) return
    item.printStatus = status
    this._notify()
  }

  _isStaleProcessing(item) {
    if (!item || item.status !== 'processing' || !item.lastAttemptAt) return false
    return Date.now() - new Date(item.lastAttemptAt).getTime() > PROCESSING_STALE_MS
  }

  _recoverStaleProcessing(force = false) {
    let recovered = 0
    this._queue.forEach(item => {
      if (force || this._isStaleProcessing(item)) {
        if (item.status !== 'processing') return
        item.status = 'pending'
        item.error = item.error || 'Guardado trabado recuperado para reintento'
        this._persistItem(item)
        recovered++
      }
    })
    if (recovered) {
      this._notify()
    }
    return recovered
  }

  _saleTotal(saleData) {
    if (!saleData || !saleData.payment) return 0
    return parseFloat(saleData.payment.total || 0) || 0
  }

  _shouldPrintTicket(saleData) {
    // Las ventas previas a esta opción no tenían la marca y ya asumían
    // impresión; sólo false explícito significa que no se solicitó.
    return !saleData || saleData.print_ticket !== false
  }

  _buildMeta(saleData) {
    return {
      total: this._saleTotal(saleData),
      cashNames: this._saleCashNames(saleData),
      cashIds: this._saleCashIds(saleData),
      itemsCount: this._saleItemsCount(saleData),
      customerName: saleData ? (saleData.customer_name || '—') : '—',
      customerDocument: saleData ? (saleData.customer_document_number || '') : '',
      pointSale: this._salePointSale(saleData),
      idempotencyKey: saleData ? (saleData._idempotency_key || '') : '',
    }
  }

  _saleCashNames(saleData) {
    if (!saleData || !saleData.payment || !saleData.payment.cash) return ''
    const detail = saleData.payment.cash.detail || []
    return detail.map(d => d.cash ? d.cash.name : '').filter(Boolean).join(', ')
  }

  _saleCashIds(saleData) {
    if (!saleData || !saleData.payment || !saleData.payment.cash) return []
    const detail = saleData.payment.cash.detail || []
    return detail.map(d => parseInt(d.cash_id)).filter(Boolean)
  }

  _saleItemsCount(saleData) {
    if (!saleData) return 0
    return (saleData.detailProducts         || []).length
      + (saleData.detailProductsCompound || []).length
      + (saleData.detailServices         || []).length
      + (saleData.detailConcepts         || []).length
  }

  _saleProducts(saleData) {
    if (!saleData) return []
    return (saleData.detailProducts || []).map(item => ({
      name: item.product ? item.product.name : 'Producto',
      code: item.product ? item.product.code : '',
      quantity: parseFloat(item.quantity || 0),
      amount: parseFloat(item.amount || 0),
      total: parseFloat((parseFloat(item.quantity || 0) * parseFloat(item.amount || 0)).toFixed(2)),
    }))
  }

  _salePointSale(saleData) {
    if (!saleData) return ''
    if (saleData.points_sales && saleData.points_sales.name) return saleData.points_sales.name
    if (saleData.points_sales && saleData.points_sales.point_sale) return saleData.points_sales.point_sale
    return saleData.points_sales_id || ''
  }

  _saleFiscalStatus(responseData) {
    if (!responseData) return 'unknown'
    if (responseData.afip_error) return 'error'
    if (responseData.afip_cae || responseData.afip) return 'authorized'
    if (responseData.PointSale && responseData.PointSale.nd) return 'not_required'
    if (responseData.point_sale && responseData.point_sale.nd) return 'not_required'
    return 'unknown'
  }

  _describeItem(item) {
    const meta = item.meta || this._buildMeta(item.saleData)
    return {
      id:           item.id,
      enqueuedAt:   item.enqueuedAt,
      status:       item.status,
      syncStage:    item.syncStage || (item.status === 'failed' ? 'sync_failed' : 'local_registered'),
      fiscalStatus: item.fiscalStatus || 'pending',
      printStatus:  item.printStatus || 'pending',
      attempts:     item.attempts || 0,
      error:        item.error || '',
      lastAttemptAt: item.lastAttemptAt || null,
      lastErrorAt:   item.lastErrorAt || null,
      stale:        this._isStaleProcessing(item),
      customerName: meta.customerName,
      customerDocument: meta.customerDocument,
      pointSale:    meta.pointSale,
      idempotencyKey: meta.idempotencyKey,
      itemsCount:   meta.itemsCount,
      products:     this._saleProducts(item.saleData),
      total:        meta.total,
      cashNames:    meta.cashNames,
      cashIds:      meta.cashIds,
      saleData:     item.saleData || null,
    }
  }

  _notify() {
    const stats = this.getStats()
    this._listeners.forEach(fn => {
      try {
        fn(stats)
      } catch(e) {
        console.warn('[SaleQueue] listener error:', e)
      }
    })
  }
}

// Singleton — una sola cola por sesión de browser
const queue = new SaleQueue()

export default queue
